import Translation from 'components/Translation'
import { useTenantContext } from 'context/TenantContext'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ClientMembershipItem } from 'types/client-membership'
import { classNames } from 'utils/dom-helpers'
import { toYmd } from 'utils/string-localization-helper'
import { default_primary_color } from '../../context/AuthContext'
import getConfig from 'next/config'

export type MembershipSelectProps = {
    options: ClientMembershipItem[]
    onSelect(i?: ClientMembershipItem): void
}
export function MembershipSelect({
    options,
    onSelect,
}: MembershipSelectProps) {
    const ref = useRef<HTMLDivElement>(null)
    const { primaryColor } = useTenantContext()
    const [displayOptions, setDisplayOptions] = useState(false)
    const [selected, setSelected] = useState<
        ClientMembershipItem | undefined
    >(undefined)
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    useEffect(() => {
        const entitled = options.find((option) => option.entitled) || undefined
        setSelected(entitled)
        onSelect(entitled)
    }, [options])

    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setDisplayOptions(false)
            }
        }
        document.addEventListener('click', handleClickOutside, true)
        return () => {
            document.removeEventListener('click', handleClickOutside, true)
        }
    }, [ref])

    function selectMembershipHandler(selected: ClientMembershipItem) {
        onSelect(selected)
        setSelected(selected)
        setDisplayOptions(false)
    }

    function RenderMembershipOptions() {
        return (
            <div className="rounded-md shadow-lg relative">
                {displayOptions && (
                    <div className="max-h-56 overflow-x-auto">
                        {options?.map((item) => (
                            <div
                                className={classNames("mt-3 p-2 hover:bg-gray-100",
                                    item.entitled ? "cursor-pointer" : "cursor-not-allowed grayscale"
                                )}
                                onClick={() => {
                                    if (item.entitled) {
                                        selectMembershipHandler(item)
                                    }
                                }}
                                key={item.id}
                            >
                                <div className="flex items-center">
                                    <div className="w-10 h-10 mr-4 overflow-hidden">
                                        <img
                                            className="rounded-md"
                                            src={
                                                item?.membership?.img ||
                                                `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/location/location_fallback.jpg`
                                            }
                                            alt="Membership Image"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex flex-col">
                                            <span>
                                                {item.membership.name}
                                            </span>
                                            <span className="text-gray-400 text-sm mt-2">
                                                <i className="feather-calendar mr-3" />
                                                <Translation content_key="booking.valid_until" />
                                                &nbsp;
                                                {toYmd(
                                                    new Date(
                                                        item.client.expires_at
                                                    )
                                                )}
                                            </span>
                                        </div>
                                        {!item.entitled && (item.content_tag || item?.not_entitled_reason) &&
                                            <Translation render_as="label" className="text-sm" content_key={item.content_tag || item.not_entitled_reason || ""} />
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    const RenderSelectedOption = () => {
        return (
            <div className="">
                {selected && !displayOptions && (
                    <div>
                        <div className="mt-3 p-2 border-2 border-green-400 rounded-md">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div className="w-10 h-10 mr-4 overflow-hidden">
                                        <img
                                            className="rounded-md"
                                            src={
                                                selected.membership.img ||
                                                `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/location/location_fallback.jpg`
                                            }
                                            alt="Membership Image"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex flex-col">
                                            <span>
                                                {selected.membership.name}
                                            </span>
                                            <span className="text-gray-400 text-sm mt-2">
                                                <i className="feather-calendar mr-3" />
                                                <Translation content_key="booking.valid_until" />
                                                &nbsp;
                                                {toYmd(
                                                    new Date(
                                                        selected.client.expires_at
                                                    )
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <i className="feather-check-circle text-green-400 text-3xl" />
                                </div>
                            </div>
                        </div>
                        <div
                            className="text-sm mt-1"
                            onClick={() => {
                                setDisplayOptions(true)
                            }}
                        >
                            <Translation content_key="booking.membership_applied" />
                            &nbsp;
                            &nbsp;
                            <span className="text-sky-500 cursor-pointer">
                                <Translation content_key="booking.change" />
                            </span>
                        </div>
                    </div>
                )}
            </div>
        )
    }
    return (
        <div ref={ref} className="w-full">
            <div className="mb-2 flex justify-between">
                <h1>
                    <Translation content_key="booking.active_memberships" /> (
                    {options.length})
                </h1>
            </div>
            {!selected && (
                <div
                    className="border-dashed border-2 rounded-md py-2"
                    onClick={() => setDisplayOptions(true)}
                >
                    <div className="flex items-center px-4 cursor-pointer">
                        <div>
                            <i
                                className="feather-shopping-bag text-red-700"
                                style={{
                                    backgroundColor: primaryColor
                                        ? `${primaryColor}90`
                                        : `${default_primary_color}90`,
                                }}
                            />
                        </div>
                        <div className="flex flex-col ml-5">
                            <span className="">
                                <Translation content_key="booking.select_membership_main_label" />
                            </span>
                            <span className="text-gray-400 text-sm">
                                <Translation content_key="booking.selected_membership_sub_label" />
                            </span>
                        </div>
                    </div>
                </div>
            )}
            {RenderMembershipOptions()}
            {RenderSelectedOption()}
        </div>
    )
}
